import React from 'react';
import styled from '@emotion/styled';
import ApplicationLayoutModel from '../../../../../shared/models/applications/api/ApplicationLayoutModel';
import { sidebarBodyTypography, sidebarCaptionTypography, sidebarFocusStyles } from './AssistantSidebar.styles';
import { color_shades_darker, color_shades_light, voicify_spark_darker, voicify_spark_lightest } from '../../../../../shared/constants/colors';

interface AssistantAnalyticsFilterProps {
    layout: ApplicationLayoutModel;
    loadedAppIds: Set<string>;
    selectedAppIds: Set<string>;
    disabled: boolean;
    onSelect: (appIds: string[], selected: boolean) => void;
    onClearAll: () => void;
    liveAppIds: Set<string>;
    showSpanishFilter?: boolean;
    spanishOnly?: boolean;
    onSpanishOnlyChange?: (selected: boolean) => void;
}

const AssistantAnalyticsFilter: React.FC<AssistantAnalyticsFilterProps> = ({ layout, loadedAppIds, selectedAppIds, disabled, onSelect, onClearAll, liveAppIds, showSpanishFilter, spanishOnly, onSpanishOnlyChange }) => {
    const categories = [...layout.groups, { id: 'ungrouped', name: 'Other', appIds: layout.ungroupedAppIds }];
    const allIds = Array.from(loadedAppIds);

    const renderOption = (id: string, label: string, appIds: string[]) => {
        const ids = appIds.filter(appId => loadedAppIds.has(appId));
        const selectedCount = ids.filter(appId => selectedAppIds.has(appId)).length;
        const isSelected = ids.length > 0 && selectedCount === ids.length;
        const isMixed = selectedCount > 0 && !isSelected;
        const isDisabled = disabled || ids.length === 0;
        return <Option key={id} $isDisabled={isDisabled} $isSelected={isSelected || isMixed}>
            <Checkbox
                type="checkbox"
                aria-label={`Include ${label} in analytics`}
                checked={isSelected}
                ref={input => { if (input) input.indeterminate = isMixed; }}
                disabled={isDisabled}
                onChange={() => onSelect(ids, !isSelected)}
            />
            <OptionName>{label}</OptionName>
            <SelectionCount aria-label={`${selectedCount} of ${ids.length} assistants selected`}>{selectedCount}/{ids.length}</SelectionCount>
        </Option>;
    };

    return <>
        <Section aria-label="Analytics assistant status selection">
            <FilterActions>
                <ClearFilters type="button" disabled={disabled} onClick={onClearAll}>Clear all filters</ClearFilters>
            </FilterActions>
            <SectionTitle>Assistant status</SectionTitle>
            {renderOption('all-statuses', 'All assistants', allIds)}
            {renderOption('live', 'Live', allIds.filter(id => liveAppIds.has(id)))}
            {renderOption('not-live', 'Not live', allIds.filter(id => !liveAppIds.has(id)))}
        </Section>
        {showSpanishFilter && <Section aria-label="Analytics language filter">
            <SectionTitle>Language</SectionTitle>
            <Option $isDisabled={disabled} $isSelected={!!spanishOnly}>
                <Checkbox
                    type="checkbox"
                    aria-label="Spanish only"
                    checked={!!spanishOnly}
                    disabled={disabled}
                    onChange={event => onSpanishOnlyChange?.(event.target.checked)}
                />
                <OptionName>Spanish only</OptionName>
            </Option>
        </Section>}
        <Section aria-label="Analytics group selection">
            <SectionTitle>Analytics groups</SectionTitle>
            {renderOption('all', 'All groups', allIds)}
            <CategoryOptions>
                {categories.filter(category => category.id !== 'ungrouped' || category.appIds.length > 0)
                    .map(category => renderOption(category.id, category.name, category.appIds))}
            </CategoryOptions>
        </Section>
    </>;
};

const Section = styled.div`
    border-top: 1px solid ${color_shades_light};
    margin-top: 4px;
    padding-top: 8px;

    &:first-of-type {
        border-top: 0;
        margin-top: 0;
        padding-top: 4px;
    }
`;
const SectionTitle = styled.div`
    ${sidebarCaptionTypography}
    padding: 0 12px 4px;
    color: ${color_shades_darker};
    letter-spacing: 0.5px;
`;
const FilterActions = styled.div`display: flex; justify-content: flex-end; padding: 0 8px 8px;`;
const ClearFilters = styled.button`
    ${sidebarCaptionTypography}
    ${sidebarFocusStyles}
    border: 0;
    border-radius: 6px;
    padding: 6px 8px;
    background: transparent;
    color: ${voicify_spark_darker};
    cursor: pointer;

    &:hover:not(:disabled) { background: ${voicify_spark_lightest}; }
    &:disabled { opacity: 0.5; cursor: default; }
`;
const CategoryOptions = styled.div`min-width: 0;`;
const Option = styled.label<{ $isDisabled: boolean; $isSelected: boolean }>`
    ${sidebarBodyTypography}
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    color: ${props => props.$isSelected ? voicify_spark_darker : color_shades_darker};
    cursor: ${props => props.$isDisabled ? 'default' : 'pointer'};
    opacity: ${props => props.$isDisabled ? 0.5 : 1};
    transition: background-color 0.2s, color 0.2s;

    &:hover, &:focus-within {
        background: ${props => props.$isDisabled ? 'transparent' : voicify_spark_lightest};
    }
`;
const Checkbox = styled.input`
    ${sidebarFocusStyles}
    flex-shrink: 0;
    appearance: none;
    position: relative;
    box-sizing: border-box;
    width: 16px;
    height: 16px;
    margin: 0;
    border: 2px solid ${color_shades_light};
    border-radius: 3px;
    background-color: white;
    cursor: inherit;
    transition: background-color 0.2s, border-color 0.2s;

    &:checked, &:indeterminate {
        background-color: ${voicify_spark_darker};
        border-color: ${voicify_spark_darker};
    }

    &:checked:not(:indeterminate)::after {
        content: '';
        position: absolute;
        left: 50%;
        top: 45%;
        width: 3px;
        height: 7px;
        border: solid white;
        border-width: 0 2px 2px 0;
        transform: translate(-50%, -50%) rotate(45deg);
    }

    &:indeterminate::after {
        content: '';
        position: absolute;
        left: 50%;
        top: 50%;
        width: 8px;
        height: 2px;
        border-radius: 1px;
        background-color: white;
        transform: translate(-50%, -50%);
    }
`;
const OptionName = styled.span`flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;`;
const SelectionCount = styled.span`${sidebarCaptionTypography} flex-shrink: 0; font-variant-numeric: tabular-nums;`;

export default AssistantAnalyticsFilter;
